import {
	ChangeDetectionStrategy,
	Component,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	OnInit,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { OrdersSettingsInterface } from '@shared/components/trading/widgets/orders-widget/core/interfaces/orders-settings.interface'
import { TuiDayRange } from '@taiga-ui/cdk'
import { debounceTime, Observable, Subject, switchMap, takeUntil } from 'rxjs'
import { PostTradingEventInterface } from '@modules/options-module/post-trading/interfaces/post-trading-event.interface'
import { PostTradingHelperService } from '@modules/options-module/post-trading/services/post-trading-helper.service'
import { PostTradingService } from '@modules/options-module/post-trading/services/post-trading.service'
import { PostTradingEventTypeEnum } from '@modules/options-module/post-trading/enums/post-trading-event-type.enum'
import { ColumnInterface } from '@shared/interfaces/column.interface'
import { ColumnsPostTradingExpiredEnum } from '@shared/components/trading/widgets/orders-widget/core/enums/post-trading/columns-post-trading-expired.enum'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { DatePipe, UpperCasePipe } from '@angular/common'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { PricePipe } from '@shared/pipes/price.pipe'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { TranslatePipe } from '@ngx-translate/core'
import { OptionTypeEnum } from '@modules/options-module/general/enums/option-type.enum'
import { PostTradingExpiredEventsActionsComponent } from '@shared/components/trading/orders/orders-options/tables/post-trading/post-trading-expired-orders/common/post-trading-expired-events-actions/post-trading-expired-events-actions.component'
import { TableColumnComponent } from '@shared/components/common/table/core/components/table-column/table-column.component'
import { TableActionsComponent } from '@shared/components/common/table/core/components/table-actions/table-actions.component'
import { TradingLauncherService } from '@shared/services/trading-launcher.service'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { SecretDirective } from '@shared/directives/secret.directive'
import { TuiSkeleton } from '@taiga-ui/kit'
import { TableFooterComponent } from '@shared/components/common/table/core/components/table-footer/table-footer.component'
import { TableComponent } from '@shared/components/common/table/table.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-post-trading-expired-orders',
	templateUrl: './post-trading-expired-orders.component.html',
	styleUrl: './post-trading-expired-orders.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		DatePipe,
		HighlightDirective,
		PricePipe,
		QuantityPipe,
		TranslatePipe,
		UpperCasePipe,
		PostTradingExpiredEventsActionsComponent,
		TableColumnComponent,
		TableActionsComponent,
		SecretDirective,
		TuiSkeleton,
		TableFooterComponent,
		TableComponent,
	],
})
export class PostTradingExpiredOrdersComponent implements OnChanges, OnInit, OnDestroy {
	protected readonly optionTypeEnum: typeof OptionTypeEnum = OptionTypeEnum
	protected readonly postTradingEventTypeEnum: typeof PostTradingEventTypeEnum = PostTradingEventTypeEnum
	protected readonly columnsPostTradingExpiredEnum: typeof ColumnsPostTradingExpiredEnum =
		ColumnsPostTradingExpiredEnum

	public widget: InputSignal<WidgetInterface<OrdersSettingsInterface> | null> =
		input<WidgetInterface<OrdersSettingsInterface> | null>(null)
	public ticker: InputSignal<string> = input<string>('')
	public datesRange: InputSignal<TuiDayRange | null> = input<TuiDayRange | null>(null)

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _tradingLauncherService: TradingLauncherService =
		inject<TradingLauncherService>(TradingLauncherService)
	private readonly _postTradingService: PostTradingService = inject<PostTradingService>(PostTradingService)
	private readonly _postTradingHelperService: PostTradingHelperService =
		inject<PostTradingHelperService>(PostTradingHelperService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)

	private _needGetPostTrading$: Subject<void> = new Subject<void>()

	protected statesColumns: WritableSignal<ColumnInterface<ColumnsPostTradingExpiredEnum>[]> = signal([])

	protected postTradingEvents: WritableSignal<PostTradingEventInterface[]> = signal<PostTradingEventInterface[]>([])
	protected isLoading: WritableSignal<boolean> = signal<boolean>(false)
	protected isSecret: WritableSignal<boolean> = signal<boolean>(false)
	private _listenSecret(): void {
		this._userHelperService.isSecret$.pipe(takeUntil(this._destroy$)).subscribe((value: boolean): void => {
			this.isSecret.set(value)
		})
	}

	private _getPostTradingEvents$(): Observable<PostTradingEventInterface[]> {
		return this._postTradingService.getPostTradingEvents(
			this._postTradingHelperService.getPostTradingEventsParams(
				this.postTradingEventTypeEnum.EXPIRED,
				this.ticker(),
				this.datesRange(),
			),
		)
	}

	private _launchPostTradingEvents(): void {
		this._tradingLauncherService
			.launchPostTradingEvents$(this.postTradingEventTypeEnum.EXPIRED)
			.pipe(takeUntil(this._destroy$))
			.subscribe(([postTradingEvents, isLoading]: [PostTradingEventInterface[], boolean]): void => {
				this.postTradingEvents.set(postTradingEvents)
				this.isLoading.set(isLoading)
			})
	}

	private _listenNeedGetPostTrading(): void {
		this._needGetPostTrading$
			.pipe(
				takeUntil(this._destroy$),
				debounceTime(500),
				switchMap((): Observable<PostTradingEventInterface[]> => this._getPostTradingEvents$()),
			)
			.subscribe()
	}

	private _updateStatesColumns(): void {
		const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()
		if (!widget) return

		this.statesColumns.set([...widget.settings.options.postTrading.expired.columns])
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['widget']) this._updateStatesColumns()
		if (changes['datesRange'] || changes['ticker']) this._needGetPostTrading$.next()
	}

	ngOnInit(): void {
		this._listenSecret()
		this._listenNeedGetPostTrading()
		this._launchPostTradingEvents()
		this._getPostTradingEvents$().pipe(takeUntil(this._destroy$)).subscribe()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
